<template>
  <div class="push-page">
    <div class="page-header">
      <div>
        <h1>Push Notifications</h1>
        <p>Send and review push notifications for your hostel guests</p>
      </div>
      <button class="btn btn-primary" @click="openCreateModal">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Send Notification
      </button>
    </div>

    <div class="kpis">
      <div class="kpi">
        <span>Total Sent</span>
        <strong>{{ totalCount }}</strong>
      </div>
      <div class="kpi">
        <span>On This Page</span>
        <strong>{{ notifications.length }}</strong>
      </div>
    </div>

    <div class="table-wrap">
      <div v-if="loading" class="table-loading-overlay">
        <div class="table-spinner">
          <div class="spinner-circle-small" />
          <span class="loading-text-small">Loading notifications...</span>
        </div>
      </div>
      <table v-if="notifications.length > 0" class="table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Message</th>
            <th>Type</th>
            <th>Status</th>
            <th>Image</th>
            <th>Sent On</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in notifications" :key="row.notificationId">
            <td class="title-cell">{{ getNotificationTitle(row) }}</td>
            <td class="message-cell">{{ row.message || '—' }}</td>
            <td>
              <span class="type-badge" :class="channelTypeClass(row.channeltype)">
                {{ formatChannelType(row.channeltype) }}
              </span>
            </td>
            <td>
              <span class="status-badge" :class="statusClass(row.status)">{{ formatStatus(row.status) }}</span>
            </td>
            <td>
              <img
                v-if="row.image"
                :src="row.image"
                alt="Notification"
                class="thumb"
                @error="onImageError"
              />
              <span v-else class="muted">—</span>
            </td>
            <td>{{ formatDate(row.createdat || row.updatedon) }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else-if="!loading" class="empty-state">
        <img src="@/assets/guest-maint-staff.svg" alt="No notifications" class="empty-illustration-img" />
        <p>No push notifications yet</p>
        <button class="btn btn-primary btn-sm" @click="openCreateModal">Send your first notification</button>
      </div>
    </div>

    <div v-if="totalPages > 1 || filters.page > 1" class="pagination-wrapper">
      <button class="pagination-btn" :disabled="filters.page <= 1" @click="changePage(filters.page - 1)">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>
      <button
        v-for="page in visiblePages"
        :key="page"
        class="pagination-btn"
        :class="{ active: page === filters.page }"
        @click="changePage(page)"
      >
        {{ page }}
      </button>
      <button class="pagination-btn" :disabled="filters.page >= totalPages" @click="changePage(filters.page + 1)">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
    </div>

    <!-- Create Modal -->
    <div v-if="showFormModal" class="modal-overlay" @click.self="closeCreateModal">
      <div class="modal">
        <div class="modal-header">
          <h3>Send Push Notification</h3>
          <button type="button" class="modal-close" aria-label="Close" @click="closeCreateModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="form-grid">
          <label for="notif-title">Title <span class="required">*</span></label>
          <input id="notif-title" v-model.trim="form.title" class="input" type="text" placeholder="Notification title" :disabled="saving" />

          <label for="notif-message">Message <span class="required">*</span></label>
          <textarea
            id="notif-message"
            v-model.trim="form.message"
            class="input textarea"
            rows="3"
            placeholder="Write your message..."
            :disabled="saving"
          />

          <label for="notif-type">Notification Type <span class="required">*</span></label>
          <select id="notif-type" v-model="form.notificationType" class="input" :disabled="saving" @change="onNotificationTypeChange">
            <option value="BROADCAST">Broadcast</option>
            <option value="GUEST">Guest</option>
          </select>

          <template v-if="form.notificationType === 'GUEST'">
            <label for="notif-guest">Active Guest <span class="required">*</span></label>
            <select id="notif-guest" v-model="form.guestId" class="input" :disabled="saving || loadingGuests">
              <option value="">{{ loadingGuests ? 'Loading guests...' : 'Select guest' }}</option>
              <option v-for="guest in activeGuests" :key="guest.guestId" :value="guest.guestId">
                {{ formatGuestLabel(guest) }}
              </option>
            </select>

            <label for="notif-action">Action Type <span class="required">*</span></label>
            <select id="notif-action" v-model="form.actionScreen" class="input" :disabled="saving">
              <option value="">Select action</option>
              <option value="HOSTEL_RENT">Hostel Rent</option>
              <option value="FOOD_MENU">Food Menu</option>
              <option value="COMPLAIN_LISTING">Complaint</option>
            </select>
          </template>

          <template v-else>
            <label for="notif-broadcast-action">Action Type</label>
            <select id="notif-broadcast-action" v-model="form.actionScreen" class="input" :disabled="saving">
              <option value="MYSPACE_HOME">General</option>
              <option value="FOOD_MENU">Food Menu</option>
            </select>
          </template>

          <label>Image</label>
          <div class="image-field">
            <div class="image-upload-area" @click="triggerImageUpload">
              <input
                ref="imageInput"
                type="file"
                accept="image/*"
                class="hidden-input"
                @change="handleImageUpload"
              />
              <div v-if="!imagePreview && !form.image" class="upload-placeholder">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <p>Click to upload image (optional)</p>
                <span class="upload-hint">JPEG, PNG, GIF, WebP</span>
              </div>
              <div v-else class="image-preview-wrap">
                <img :src="imagePreview || ''" alt="Preview" class="preview-img" />
                <div v-if="uploadingImage" class="upload-overlay">
                  <div class="spinner-circle-small" />
                  <span>Uploading...</span>
                </div>
              </div>
            </div>
            <button
              v-if="form.image || imagePreview"
              type="button"
              class="mini mini-clear remove-image-btn"
              :disabled="saving || uploadingImage"
              @click="clearImage"
            >
              Remove image
            </button>
          </div>
        </div>

        <div class="modal-actions">
          <button type="button" class="mini" :disabled="saving" @click="closeCreateModal">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="saving || uploadingImage" @click="submitNotification">
            {{ saving ? 'Sending...' : 'Send Notification' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="toast.message" :class="['toast', toast.type]">{{ toast.message }}</div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '@/utilities/errorHelper';

const GUEST_ACTION_SCREENS = ['HOSTEL_RENT', 'FOOD_MENU', 'COMPLAIN_LISTING'];
const BROADCAST_ACTION_SCREENS = ['MYSPACE_HOME', 'FOOD_MENU'];

export default {
  name: 'PushNotificationView',
  data() {
    return {
      notifications: [],
      totalCount: 0,
      loading: false,
      saving: false,
      loadingGuests: false,
      uploadingImage: false,
      showFormModal: false,
      activeGuests: [],
      imagePreview: null,
      filters: { page: 1, pagesize: 15 },
      form: {
        title: '',
        message: '',
        notificationType: 'BROADCAST',
        guestId: '',
        actionScreen: 'MYSPACE_HOME',
        image: '',
      },
      toast: { message: '', type: 'success' },
    };
  },
  computed: {
    objectId() {
      return this.$route.params.id || this.$route.query.objectid || '';
    },
    totalPages() {
      if (this.totalCount > 0) {
        return Math.max(1, Math.ceil(this.totalCount / this.filters.pagesize));
      }
      if (this.notifications.length === 0) return Math.max(1, this.filters.page);
      return this.notifications.length < this.filters.pagesize ? this.filters.page : this.filters.page + 1;
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 4;
      let start = Math.max(1, this.filters.page - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      for (let i = start; i <= end; i += 1) pages.push(i);
      return pages;
    },
  },
  mounted() {
    this.fetchNotifications();
  },
  methods: {
    getNotificationTitle(row) {
      return row.title || row.tiltle || '—';
    },
    formatChannelType(channeltype) {
      const type = String(channeltype || '').toUpperCase();
      if (type === 'OBJECT' || type === 'BROADCAST') return 'Broadcast';
      if (type === 'SUBSCRIBER') return 'Guest';
      return channeltype || '—';
    },
    formatStatus(status) {
      const s = String(status || '').toUpperCase();
      if (s === 'SEND' || s === 'SENT') return 'Sent';
      if (s === 'FAILED') return 'Failed';
      if (s === 'PENDING') return 'Pending';
      return status || '—';
    },
    channelTypeClass(channeltype) {
      if (channeltype === 'SUBSCRIBER') return 'type-guest';
      return 'type-broadcast';
    },
    statusClass(status) {
      const s = String(status || '').toUpperCase();
      if (s === 'SEND' || s === 'SENT') return 'status-sent';
      if (s === 'FAILED') return 'status-failed';
      return 'status-default';
    },
    formatDate(value) {
      if (!value) return '—';
      const d = new Date(String(value).replace(' ', 'T'));
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString('en-NP', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatGuestLabel(guest) {
      const id = guest.guestId || '—';
      const name = guest.firstName != null && String(guest.firstName).trim()
        ? String(guest.firstName).trim()
        : '—';
      const room = guest.roomNumber != null && String(guest.roomNumber).trim()
        ? String(guest.roomNumber).trim()
        : '—';
      return `${id} - ${name} - ${room}`;
    },
    showToast(message, type = 'success') {
      this.toast = { message, type };
      window.setTimeout(() => {
        if (this.toast.message === message) this.toast.message = '';
      }, 3500);
    },
    onImageError(event) {
      event.target.style.display = 'none';
    },
    async fetchNotifications() {
      if (!this.objectId) {
        this.notifications = [];
        this.totalCount = 0;
        return;
      }
      this.loading = true;
      try {
        const response = await apiService.getNotifications({
          objectid: this.objectId,
          page: this.filters.page,
          pagesize: this.filters.pagesize,
        });
        const body = response?.data || {};
        this.totalCount = Number(body.totalcount) || 0;
        this.notifications = Array.isArray(body.data) ? body.data : [];
      } catch (error) {
        if (isNoDataError(error)) {
          this.notifications = [];
          this.totalCount = 0;
        } else if (!isHmsSubscriptionInactiveError(error)) {
          this.showToast(error?.response?.data?.reason || 'Unable to load notifications', 'error');
        }
      } finally {
        this.loading = false;
      }
    },
    async fetchActiveGuests() {
      if (!this.objectId) return;
      this.loadingGuests = true;
      try {
        const response = await apiService.getGuests({
          objectId: this.objectId,
          page: 1,
          pagesize: 100,
          status: 'ACTIVE',
        });
        const data = response?.data?.data;
        this.activeGuests = Array.isArray(data) ? data : [];
      } catch (error) {
        if (isNoDataError(error)) {
          this.activeGuests = [];
        } else if (!isHmsSubscriptionInactiveError(error)) {
          this.showToast(error?.response?.data?.reason || 'Unable to load active guests', 'error');
        }
      } finally {
        this.loadingGuests = false;
      }
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.filters.page = page;
      this.fetchNotifications();
    },
    resetForm() {
      this.form = {
        title: '',
        message: '',
        notificationType: 'BROADCAST',
        guestId: '',
        actionScreen: 'MYSPACE_HOME',
        image: '',
      };
      this.imagePreview = null;
      if (this.$refs.imageInput) this.$refs.imageInput.value = '';
    },
    openCreateModal() {
      this.resetForm();
      this.showFormModal = true;
      this.fetchActiveGuests();
    },
    closeCreateModal(force = false) {
      if (!force && this.saving) return;
      this.showFormModal = false;
      this.resetForm();
    },
    onNotificationTypeChange() {
      this.form.guestId = '';
      this.form.actionScreen = this.form.notificationType === 'BROADCAST' ? 'MYSPACE_HOME' : '';
    },
    triggerImageUpload() {
      if (this.saving || this.uploadingImage) return;
      this.$refs.imageInput?.click();
    },
    async handleImageUpload(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        this.showToast('Please select a valid image file', 'error');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        this.imagePreview = e.target.result;
      };
      reader.readAsDataURL(file);

      this.uploadingImage = true;
      try {
        const formData = new FormData();
        formData.append('files', file);
        const uploadResponse = await apiService.uploadImage(formData);
        const uploadedFilenames = uploadResponse.data || [];
        if (uploadedFilenames.length > 0) {
          this.form.image = uploadedFilenames[0];
        } else {
          throw new Error('No filename returned from upload');
        }
      } catch (error) {
        console.error('Image upload failed:', error);
        this.imagePreview = null;
        this.form.image = '';
        this.showToast('Failed to upload image. Please try again.', 'error');
      } finally {
        this.uploadingImage = false;
        if (this.$refs.imageInput) this.$refs.imageInput.value = '';
      }
    },
    clearImage() {
      this.form.image = '';
      this.imagePreview = null;
      if (this.$refs.imageInput) this.$refs.imageInput.value = '';
    },
    buildPayload() {
      const isBroadcast = this.form.notificationType === 'BROADCAST';
      const payload = {
        mode: 'PUSH',
        title: this.form.title,
        message: this.form.message,
        action: {
          type: 'OPEN_SCREEN',
          screen: this.form.actionScreen,
        },
        objectid: this.objectId,
        channeltype: isBroadcast ? 'OBJECT' : 'SUBSCRIBER',
        channel: isBroadcast ? this.objectId : this.form.guestId,
      };
      if (this.form.image) {
        payload.image = this.form.image;
      }
      return payload;
    },
    validateForm() {
      if (!this.form.title.trim()) {
        this.showToast('Title is required', 'error');
        return false;
      }
      if (!this.form.message.trim()) {
        this.showToast('Message is required', 'error');
        return false;
      }
      if (!this.objectId) {
        this.showToast('Object context is missing', 'error');
        return false;
      }
      const allowedScreens = this.form.notificationType === 'BROADCAST'
        ? BROADCAST_ACTION_SCREENS
        : GUEST_ACTION_SCREENS;

      if (this.form.notificationType === 'GUEST' && !this.form.guestId) {
        this.showToast('Please select an active guest', 'error');
        return false;
      }
      if (!allowedScreens.includes(this.form.actionScreen)) {
        this.showToast('Please select an action type', 'error');
        return false;
      }
      return true;
    },
    async submitNotification() {
      if (!this.validateForm()) return;
      this.saving = true;
      try {
        await apiService.createNotification(this.buildPayload());
        this.showToast('Notification sent successfully', 'success');
        this.closeCreateModal(true);
        this.filters.page = 1;
        await this.fetchNotifications();
      } catch (error) {
        if (!isHmsSubscriptionInactiveError(error)) {
          this.showToast(error?.response?.data?.reason || 'Failed to send notification', 'error');
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.push-page {
  padding: 20px;
  background: #f8fafc;
  min-height: 100%;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.page-header h1 {
  margin: 0;
  font-size: 20px;
  color: #1e293b;
}
.page-header p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 13px;
}
.btn {
  border: 0;
  border-radius: 8px;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.btn-sm {
  padding: 8px 12px;
  font-size: 13px;
}
.btn-primary {
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: #fff;
}
.kpis {
  display: grid;
  grid-template-columns: repeat(2, minmax(140px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
  max-width: 420px;
}
.kpi {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
}
.kpi span {
  display: block;
  font-size: 12px;
  color: #64748b;
}
.kpi strong {
  display: block;
  font-size: 18px;
  color: #1e293b;
  margin-top: 4px;
}
.table-wrap {
  position: relative;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: auto;
  min-height: 220px;
}
.table {
  width: 100%;
  border-collapse: collapse;
}
.table th,
.table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
  font-size: 13px;
  vertical-align: middle;
}
.table th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
}
.title-cell {
  font-weight: 600;
  color: #1e293b;
  max-width: 180px;
}
.message-cell {
  max-width: 280px;
  white-space: normal;
  color: #475569;
}
.type-badge,
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.type-broadcast {
  background: #ede9fe;
  color: #5b21b6;
}
.type-guest {
  background: #dbeafe;
  color: #1d4ed8;
}
.status-sent {
  background: #dcfce7;
  color: #15803d;
}
.status-failed {
  background: #fee2e2;
  color: #b91c1c;
}
.status-default {
  background: #f1f5f9;
  color: #475569;
}
.thumb {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}
.muted {
  color: #94a3b8;
}
.table-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}
.table-spinner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.spinner-circle-small {
  width: 20px;
  height: 20px;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #5b2e8e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
.loading-text-small {
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
}
.empty-state {
  padding: 32px 16px;
  text-align: center;
  color: #64748b;
}
.empty-illustration-img {
  width: 200px;
  height: 200px;
  max-width: 100%;
  object-fit: contain;
  margin-bottom: 12px;
}
.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
}
.pagination-btn {
  min-width: 40px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.pagination-btn:hover:not(:disabled) {
  border-color: #5b2e8e;
  color: #5b2e8e;
}
.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: #f8fafc;
}
.pagination-btn.active {
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: white;
  border-color: #5b2e8e;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}
.modal {
  width: min(640px, 94vw);
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2);
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #1e293b;
}
.modal-close {
  border: none;
  background: #f1f5f9;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
}
.form-grid {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 12px;
  align-items: start;
}
.form-grid label {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  padding-top: 10px;
}
.required {
  color: #dc2626;
}
.input {
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}
.input:focus {
  outline: none;
  border-color: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12);
}
.textarea {
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}
.readonly-field {
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #64748b;
}
.image-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.image-upload-area {
  border: 2px dashed #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.image-upload-area:hover {
  border-color: #7c3aed;
  background: #faf5ff;
}
.hidden-input {
  display: none;
}
.upload-placeholder {
  text-align: center;
  color: #64748b;
}
.upload-placeholder p {
  margin: 8px 0 4px;
  font-size: 14px;
  font-weight: 500;
}
.upload-hint {
  font-size: 12px;
  color: #94a3b8;
}
.image-preview-wrap {
  position: relative;
  width: 100%;
}
.preview-img {
  max-width: 100%;
  max-height: 160px;
  border-radius: 8px;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}
.upload-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
}
.mini {
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  padding: 7px 10px;
  cursor: pointer;
  font-size: 13px;
}
.mini-clear {
  background: #f3e8ff;
  border-color: #d8b4fe;
  color: #6b21a8;
  align-self: flex-start;
}
.remove-image-btn {
  margin-top: 0;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}
.toast {
  position: fixed;
  right: 16px;
  top: 84px;
  padding: 12px 16px;
  border-radius: 8px;
  color: #fff;
  z-index: 1300;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}
.toast.success {
  background: #16a34a;
}
.toast.error {
  background: #dc2626;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
  .form-grid label {
    padding-top: 0;
  }
  .kpis {
    max-width: none;
    grid-template-columns: 1fr 1fr;
  }
}
</style>
